<template>
    <FileLoading
        v-if="isLoadingAddFileForm"
        class="h-[50vh]"
    />

    <div v-show="!isLoadingAddFileForm">
        <GeneralAddFileError v-if="!isLegacyAddFileExperienceEnabled" />

        <AddFileFormHeader :viewOrderUrl="routes.viewOrderUrl" />

        <form
            id="addClientForm"
            enctype="multipart/form-data"
            onsubmit="return false;"
        >
            <CsrfField />

            <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
            <input
                id="amount_down"
                name="amount_down"
                type="hidden"
                :value="addFileStore.tpsOrder?.amount_down"
            />
            <input
                id="earnest_money"
                name="earnest_money"
                type="hidden"
                :value="addFileStore.tpsOrder?.earnest_money"
            />
            <input
                id="sales_price"
                name="sales_price"
                type="hidden"
                :value="addFileStore.tpsOrder?.sales_price"
            />
            <input
                id="vendor"
                name="vendor"
                type="hidden"
                :value="addFileStore.tpsOrder?.vendor"
            />
            <input
                id="vendor_order_id"
                name="vendor_order_id"
                type="hidden"
                :value="addFileStore.tpsOrder?.vendor_order_id"
            />
            <input
                id="vendor_transaction_id"
                name="vendor_transaction_id"
                type="hidden"
                :value="addFileStore.tpsOrder?.vendor_transaction_id"
            />

            <FileDetailsSection />

            <!-- @todo [CLENG-3821] remove v-if -->
            <!-- @todo [CLENG-1787] remove key -->
            <ClientsSection
                v-if="!isLoadingAddFileForm"
                :key="addFileStore.clientsSectionRemountKey"
                :minVisibleClientInfoForms
            />

            <SendWireInstructionsSection v-show="!addFileStore.tpsOrder?.hideSendWire" />

            <RequestWireInformationSection v-show="!addFileStore.tpsOrder?.hideRecvWire" />

            <RequestIdentityVerificationSection />

            <RequestMortgageInformationSection v-if="featureFlagStore.flags?.enable_seller_auth_task" />

            <slot />
        </form>

        <NotificationSection
            v-if="!isLegacyAddFileExperienceEnabled"
            class="border-t"
        >
            <template #formAction>
                <DsButton
                    data-testid="add-file-submit"
                    :loading="isSubmitting"
                    size="md"
                    variant="primary"
                    @click="validateAndConfirm"
                >
                    Create file
                </DsButton>
            </template>
        </NotificationSection>
    </div>

    <template v-if="!isLegacyAddFileExperienceEnabled">
        <AddFileConfirmationDialog
            v-model:open="showSummaryDialog"
            :primaryLoading="isSubmitting"
            @confirm="confirmSummary"
        />

        <SimilarEmdDialog
            v-model:open="showSimilarEmdDialog"
            :fullFileFormData="fileFormData"
            :similarEmds
            :submitFormWith
        />
    </template>

    <EditAvailableClientSheet
        v-model:open="isEditClientSheetOpen"
        :clientValue="clientValueBeingEdited"
    />

    <DsToast class="md:top-16" />
</template>

<script setup lang="ts">
import { registerAddFileSubmittedAction } from '@/company-portal/add-file/add-file-reset';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type { AddFileRoutes, MultipleSelectClient } from '@/company-portal/add-file/add-file.types';
import type { UseAddFileSubmitOptions } from '@/company-portal/add-file/use-add-file-submit';
import { useAddFileSubmit } from '@/company-portal/add-file/use-add-file-submit';
import type { ClientInfoFormOptions } from '@/company-portal/add-file/use-client-info-forms';
import CsrfField from '@/components/auth/CsrfField.vue';
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsToast from '@/components/design-system/toast/DsToast.vue';
import AddFileFormHeader from '@/components/Views/Company/AddFile/AddFileFormHeader.vue';
import ClientsSection from '@/components/Views/Company/AddFile/ClientsSection.vue';
import AddFileConfirmationDialog from '@/components/Views/Company/AddFile/ConfirmationDialog/AddFileConfirmationDialog.vue';
import EditAvailableClientSheet from '@/components/Views/Company/AddFile/EditClient/EditAvailableClientSheet.vue';
import FileDetailsSection from '@/components/Views/Company/AddFile/FileDetailsSection.vue';
import GeneralAddFileError from '@/components/Views/Company/AddFile/GeneralAddFileError.vue';
import NotificationSection from '@/components/Views/Company/AddFile/NotificationSection.vue';
import RequestIdentityVerificationSection from '@/components/Views/Company/AddFile/RequestIdentityVerificationSection.vue';
import RequestMortgageInformationSection from '@/components/Views/Company/AddFile/RequestMortgageInformationSection.vue';
import RequestWireInformationSection from '@/components/Views/Company/AddFile/RequestWireInformationSection.vue';
import SendWireInstructionsSection from '@/components/Views/Company/AddFile/SendWireInstructionsSection.vue';
import SimilarEmdDialog from '@/components/Views/Company/AddFile/SimilarEmdDialog.vue';
import FileLoading from '@/components/Views/Company/File/FileLoading.vue';
import { EDIT_AVAILABLE_CLIENT_SHEET_INJECTION_KEY } from '@/injection-keys';
import { useFeatureFlagsStore } from '@/stores/company/feature-flags.store';
import { useUserStore } from '@/stores/company/user.store';
import { computed, onMounted, onUnmounted, provide, ref } from 'vue';

const props = defineProps<{
    isLegacyAddFileExperienceEnabled?: boolean;
    minVisibleClientInfoForms: ClientInfoFormOptions['minVisibleClientInfoForms'];
    routes: AddFileRoutes;
    tpsOrderJsonString: string;
}>();

const addFileStore = useAddFileStore();
const userStore = useUserStore();
const featureFlagStore = useFeatureFlagsStore();

userStore.getActiveCompanies();
userStore.getActiveBranches();
userStore.getAvailableWireDocs();

const isLoadingAddFileForm = computed(
    () =>
        userStore.isFetchingMeDetail ||
        userStore.isFetchingCompanies ||
        userStore.isFetchingBranches ||
        userStore.isFetchingWireDocs ||
        addFileStore.isLoadingTpsOrder,
);

// legacy: loads tps from injected string. To be decommissioned under CLENG-3276
const loadTpsOrder = async () => {
    await featureFlagStore.ensureLoaded();
    if (featureFlagStore.flags?.load_tps_order_from_api && props.routes.loadTpsOrderRoute) {
        addFileStore.loadTpsOrderRoute = props.routes.loadTpsOrderRoute;
        return;
    }
    addFileStore.ingestTpsOrder(props.tpsOrderJsonString);
};

onMounted(() => {
    loadTpsOrder();
});

// TPS orders submit through the vendor route, whose middleware resolves vendor document temp-paths to files.
// Reactive: the TPS order arrives asynchronously (api fetch or the flag-gated legacy ingest),
// so this must re-evaluate once it lands — otherwise submit routes never flip to the vendor ones.

const submitRoutes = computed<UseAddFileSubmitOptions>(() => {
    if (addFileStore.tpsOrder) {
        return {
            validateRoute: props.routes.vendorValidateRoute,
            submitRoute: props.routes.vendorSubmitRoute,
        };
    }
    return {
        validateRoute: props.routes.validateRoute,
        submitRoute: props.routes.submitRoute,
    };
});

// submission flow
const {
    isSubmitting,
    similarEmds,
    fileFormData,
    validateAndConfirm,
    confirmSummary,
    submitFormWith,
    showSummaryDialog,
    showSimilarEmdDialog,
} = useAddFileSubmit(submitRoutes);

// edit client sheet
const isEditClientSheetOpen = ref(false);
const clientValueBeingEdited = ref<MultipleSelectClient['value']>();

provide(EDIT_AVAILABLE_CLIENT_SHEET_INJECTION_KEY, {
    editAvailable: true,
    openEditClientSheet: (clientValue: MultipleSelectClient['value']) => {
        clientValueBeingEdited.value = clientValue;
        isEditClientSheetOpen.value = true;
    },
});

// form reset
const unregisterAddFileSubmittedAction = registerAddFileSubmittedAction(addFileStore.resetAddFileStore);

onUnmounted(unregisterAddFileSubmittedAction);
</script>
